// FantasyPRIME Type Definitions

// ==================== ENUMS ====================

export type CardRarity = 'PROSPECT' | 'ROOKIE' | 'VETERAN' | 'ALL_STAR' | 'GOAT';
export type CardEdition = 'BASE' | 'ROOKIE' | 'MOMENT' | 'PLAYOFF' | 'LEGEND';
export type CardType = 'BASE' | 'INSERT' | 'MOMENT' | 'AWARD' | 'SIGNATURE';
// CardCollection - using string for flexibility, but 'BASE' is the only active collection
// Future collections: PRIZM, SELECT, MOMENTS, PLAYOFFS, CHAMPIONS, SIGNATURE
export type CardCollection = string;
export type PackType = 'STANDARD' | 'PREMIUM' | 'LEGENDARY' | 'SEASONAL' | 'EVENT';
export type ListingStatus = 'ACTIVE' | 'SOLD' | 'CANCELLED' | 'EXPIRED';
export type CompetitionType = 'LEAGUE' | 'WEEKLY_EVENT' | 'DAILY_EVENT' | 'TOURNAMENT';
export type LockStatus = 'ACTIVE' | 'RELEASED' | 'FORCE_RELEASED';
export type CardDomain = 'WEB2' | 'WEB3';

// ==================== CARD METADATA (v1) ====================
// Flexible metadata structure - can be extended as needed

export interface CardMetadata {
  // Core attributes (required)
  serialNumber: number;        // Unique number within supply (e.g., #42)
  totalSupply: number;         // Max cards of this type (e.g., 100)
  year: number;                // Season/year (e.g., 2025)
  playerId: string;            // Reference to player
  cardType: CardType;          // BASE, INSERT, MOMENT, AWARD, SIGNATURE
  rarity: CardRarity;          // CORE, RARE, ELITE, LEGENDARY, PRIME
  collection: string;          // Set name (e.g., "Prizm", "Select", "Opening Day")
  edition: CardEdition;        // BASE, ROOKIE, MOMENT, PLAYOFF, LEGEND
  imageUrl: string;            // Link to card artwork

  // Optional/extensible attributes
  animationUrl?: string;       // For premium animated cards
  week?: number;               // For moment cards tied to specific week
  parallel?: string;           // Future: color variants (Silver, Gold, etc.)
  insertSet?: string;          // Future: themed subset name
  attributes?: Record<string, unknown>; // Flexible bucket for future metadata
}

// ==================== CARD SYSTEM ====================

export interface Card {
  id: string;

  // Core metadata
  metadata: CardMetadata;

  // Denormalized for queries (from metadata)
  playerId: string;
  player?: Player;
  season: number;
  rarity: CardRarity;
  edition: CardEdition;
  serialNumber: number;
  totalSupply: number;
  collection: string;
  cardType: CardType;
  imageUrl: string;

  // Ownership
  ownerId?: string;

  // Domain (WEB2 = in-app only, WEB3 = on-chain)
  domain: CardDomain;

  // WEB2 activation state
  tradeUnlockedAt?: Date;          // When card was activated for competition use
  tradeUnlockSource?: 'INDIVIDUAL' | 'BULK' | 'PACK' | 'PROMO';
  activationCostPaid?: number;

  // WEB3 staking state
  stakedAt?: Date;
  stakedTxHash?: string;

  // Lock state (for competitions)
  isLocked: boolean;               // Quick check - is card locked to any competition?
  lockCount: number;               // Number of active locks
  locks?: CardLock[];              // Active locks (when fetched with includes)

  // Blockchain state (for hybrid model)
  isOnChain: boolean;
  onChainTokenId?: string;
  onChainContract?: string;
  onChainTxHash?: string;
  redeemedAt?: Date;

  // Tradeable state
  isTradeable: boolean;            // On marketplace (WEB3 only)

  // Card state
  isBurned: boolean;
  burnedAt?: Date;

  // Timestamps
  acquiredAt: Date;
  createdAt: Date;
}

// Card lock for competition tracking
export interface CardLock {
  id: string;
  cardId: string;
  competitionType: CompetitionType;
  competitionId: string;
  competitionName?: string;        // Denormalized for display
  userId: string;
  status: LockStatus;
  lockedAt: Date;
  releaseAt?: Date;                // Expected release (competition end time)
  releasedAt?: Date;               // Actual release time
  releaseReason?: string;
}

// Competition icons for UI
export const COMPETITION_ICONS: Record<CompetitionType, string> = {
  LEAGUE: 'trophy',
  WEEKLY_EVENT: 'calendar',
  DAILY_EVENT: 'sun',
  TOURNAMENT: 'crown',
};

// Card display status - computed from state
export type CardDisplayStatus =
  | 'inactive'              // WEB2, not activated
  | 'ready'                 // Available for competitions
  | 'active_in_competition' // Locked to 1+ competitions
  | 'trading_block'         // On marketplace (WEB3 only)
  | 'staking_pending';      // Staking transaction pending

// Utility to compute display status
export function getCardDisplayStatus(card: Card): CardDisplayStatus {
  // WEB3 on marketplace
  if (card.domain === 'WEB3' && card.isTradeable) {
    return 'trading_block';
  }

  // Has active locks
  if (card.isLocked || card.lockCount > 0) {
    return 'active_in_competition';
  }

  // WEB2 not activated
  if (card.domain === 'WEB2' && !card.tradeUnlockedAt) {
    return 'inactive';
  }

  return 'ready';
}

export interface CardSupply {
  playerId: string;
  season: number;
  rarity: CardRarity;
  collection: string;
  cardType: CardType;
  maxSupply: number;
  currentSupply: number;
}

// Rarity configuration - supply caps and visual styling
// Note: edgeBonus/gameplay mechanics TBD - not included yet
//
// defaultSupply values are a MIRROR of apps/nextjs
// src/config/game.config.ts MAX_SUPPLY_PER_PLAYER — keep in sync. jest-client
// cannot import nextjs source, so these literals are a deliberate copy of the
// canonical table (500/200/75/32/13); the nextjs test
// src/config/game.config.test.ts pins those values so any change is deliberate
// and must be applied here (and in src/config/game.config.ts RARITY_SUPPLY) too.
export const RARITY_CONFIG: Record<CardRarity, {
  defaultSupply: number;  // Default max supply per player (can be overridden per card)
  color: string;
  gradient: string;
  label: string;
}> = {
  PROSPECT: {
    defaultSupply: 500,    // Common cards
    color: '#9CA3AF',
    gradient: 'from-gray-400 to-gray-600',
    label: 'Prospect',
  },
  ROOKIE: {
    defaultSupply: 200,    // Uncommon
    color: '#3B82F6',
    gradient: 'from-blue-400 to-blue-600',
    label: 'Rookie',
  },
  VETERAN: {
    defaultSupply: 75,     // Scarce
    color: '#8B5CF6',
    gradient: 'from-purple-400 to-purple-600',
    label: 'Veteran',
  },
  ALL_STAR: {
    defaultSupply: 32,     // Very scarce
    color: '#F59E0B',
    gradient: 'from-amber-400 to-orange-500',
    label: 'All-Star',
  },
  GOAT: {
    defaultSupply: 13,     // Ultra rare
    color: '#EF4444',
    gradient: 'from-red-400 via-pink-500 to-purple-500',
    label: 'G.O.A.T.',
  },
};

// Collection/Set definitions - can be expanded
export const COLLECTIONS = {
  PRIZM: { name: 'Prizm', description: 'Premium chrome cards' },
  SELECT: { name: 'Select', description: 'Tiered level cards' },
  OPENING_DAY: { name: 'Opening Day', description: 'Season launch collection' },
  PLAYOFFS: { name: 'Playoffs', description: 'Postseason moments' },
  CHAMPIONS: { name: 'Champions', description: 'Championship celebration' },
} as const;

export type CollectionKey = keyof typeof COLLECTIONS;

// ==================== PLAYER DATA ====================

export interface Player {
  id: string;
  espnId: string;
  name: string;
  team: string;
  position: 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DST';
  jerseyNumber?: number;
  imageUrl?: string;
  seasonStats?: PlayerStats;
  weeklyStats?: PlayerStats;
  projectedPoints?: number;
  injuryStatus?: string;
  isActive: boolean;
}

export interface PlayerStats {
  passingYards?: number;
  passingTds?: number;
  rushingYards?: number;
  rushingTds?: number;
  receivingYards?: number;
  receivingTds?: number;
  receptions?: number;
  fantasyPoints?: number;
}

// ==================== USER ====================

export interface User {
  id: string;
  email: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  primeCoins: number;
  primeGems: number;
  level: number;
  xp: number;
  totalXpEarned: number;
  prestigeLevel: number;
  loginStreak: number;
  longestLoginStreak: number;
  lastLoginDate?: Date;
  streakShields: number;
  seasonPassTier: 'FREE' | 'PRIME';
  seasonPassXp: number;
  packsSinceLastLegendary: number;
}

// ==================== PACK SYSTEM ====================

export interface PackTemplate {
  id: string;
  name: string;
  packType: PackType;
  description?: string;
  imageUrl?: string;
  cardCount: number;
  guaranteedRarity?: CardRarity;
  coinCost?: number;
  gemCost?: number;
  isAvailable: boolean;
  tournamentEntries: number;
}

export interface PackOpen {
  id: string;
  userId: string;
  templateId: string;
  template: PackTemplate;
  cards: Card[];
  coinsPaid: number;
  gemsPaid: number;
  openedAt: Date;
}

export interface PackOpenResult {
  cards: Card[];
  pityProgress: number;
  pityTriggered: boolean;
}

// ==================== MARKETPLACE ====================

export interface MarketListing {
  id: string;
  cardId: string;
  card: Card;
  sellerId: string;
  buyerId?: string;
  priceCoins: number;
  status: ListingStatus;
  listedAt: Date;
  soldAt?: Date;
  expiresAt: Date;
  marketplaceFee: number;
}

// ==================== PICK'EM ====================

export interface PickemSlate {
  id: string;
  season: number;
  week: number;
  name: string;
  lockTime: Date;
  endTime: Date;
  isActive: boolean;
  isScored: boolean;
  props: PickemProp[];
}

export interface PickemProp {
  id: string;
  slateId: string;
  playerId: string;
  player: Player;
  statType: string;
  line: number;
  actualValue?: number;
}

export interface PickemEntry {
  id: string;
  userId: string;
  slateId: string;
  entryFee: number;
  pickCount: number;
  correctPicks: number;
  score: number;
  payout: number;
  edgeBonusTotal: number;
  picks: PickemPick[];
}

export interface PickemPick {
  id: string;
  entryId: string;
  propId: string;
  prop: PickemProp;
  selection: 'OVER' | 'UNDER';
  cardUsed?: string;
  edgeBonus: number;
  isCorrect?: boolean;
}

// ==================== COMPETITION SHARED TYPES ====================

// Card integration modes
export type CardIntegration = 'NONE' | 'OPTIONAL_BOOST' | 'REQUIRED' | 'EDITION_EXCLUSIVE';

// Rarity gates for competitions
export type RarityGate = 'OPEN' | 'RARE_PLUS' | 'ELITE_PLUS' | 'LEGENDARY_PLUS' | 'PRIME_ONLY';

export interface PrizePool {
  prizeType: 'COINS' | 'PACKS' | 'GRADED_CARD' | 'MIXED';
  totalValue: number;           // Estimated total value
  distribution: PrizeDistribution[];
}

export interface PrizeDistribution {
  placement: string;            // "1st", "2nd-3rd", "4th-10th"
  prizeType: 'COINS' | 'PACK' | 'GRADED_CARD' | 'EXCLUSIVE_CARD';
  amount?: number;              // For coins
  description?: string;         // "PSA 10 Rookie Card"
  estimatedValue?: number;      // USD value estimate
}

// Backward compatibility alias
export type LeaguePrizePool = PrizePool;

// ==================== CHAT ====================

export type MessageType = 'TEXT' | 'CARD_SHARE' | 'GIF' | 'POLL' | 'TRASH_TALK';

export interface ChatMessage {
  id: string;
  leagueId: string;
  userId: string;
  user: User;
  content: string;
  messageType: MessageType;
  metadata?: Record<string, unknown>;
  sentAt: Date;
  editedAt?: Date;
}

// ==================== PROGRESSION ====================

export type AchievementCategory = 'COLLECTOR' | 'COMPETITOR' | 'SOCIAL' | 'LOYALTY';
export type AchievementTier = 'BRONZE' | 'SILVER' | 'GOLD' | 'DIAMOND';
export type QuestType = 'DAILY' | 'WEEKLY' | 'SEASONAL';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: AchievementCategory;
  tier: AchievementTier;
  requirement: {
    type: string;
    count: number;
  };
  xpReward: number;
  coinReward: number;
  iconUrl?: string;
}

export interface UserAchievement {
  id: string;
  achievementId: string;
  achievement: Achievement;
  progress: number;
  isUnlocked: boolean;
  unlockedAt?: Date;
}

export interface Quest {
  id: string;
  name: string;
  description: string;
  questType: QuestType;
  requirement: {
    type: string;
    count: number;
  };
  xpReward: number;
  coinReward: number;
}

export interface QuestProgress {
  id: string;
  questId: string;
  quest: Quest;
  progress: number;
  isCompleted: boolean;
  completedAt?: Date;
}

export interface SeasonPass {
  id: string;
  season: number;
  name: string;
  startDate: Date;
  endDate: Date;
  maxLevel: number;
  xpPerLevel: number;
  rewards: SeasonPassReward[];
}

export interface SeasonPassReward {
  id: string;
  level: number;
  track: 'FREE' | 'PRIME';
  rewardType: 'COINS' | 'GEMS' | 'PACK' | 'CARD' | 'COSMETIC';
  rewardData: Record<string, unknown>;
}

// ==================== EVENTS & TOURNAMENTS ====================

// Event categories
export type EventCategory = 'DAILY' | 'WEEKLY' | 'WEEKEND' | 'SPECIAL' | 'TOURNAMENT';

// Event formats (weekly)
export type EventFormat =
  | 'PICKEM'           // Traditional over/under pick'em (fixed points per hit)
  | 'PROP_BOOST'       // Odds-based props with multipliers (variable bonus points)
  | 'SHOWDOWN'         // Salary cap lineup (DFS style)
  | 'SURVIVOR'         // Pick team to win each week
  | 'BRACKET'          // Elimination bracket tournament
  | 'ACCUMULATOR';     // Points accumulated over time

// Event tiers
export type EventTier = 'STANDARD' | 'PREMIUM' | 'ELITE';

export interface EventEntryRequirements {
  tier: EventTier;
  ticketCost: number;
  coinCost: number;
  gemCost?: number;             // Optional gem cost
  minCollectorScore: number;
  cardOwnershipRequired: boolean;
  requiredCardCount?: number;   // Number of cards required for entry
  rarityGate?: RarityGate;      // Minimum rarity gate for cards
}

export interface Event {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  sport?: string;

  // Categorization
  category: EventCategory;
  format: EventFormat;
  tier: EventTier;

  // Timing
  season: number;
  week?: number;                // For weekly events
  entryOpenTime: Date;
  entryDeadline: Date;
  startTime: Date;
  endTime: Date;

  // Capacity
  maxEntries: number;
  currentEntries: number;
  unlimitedEntries: boolean;    // For free weekly events

  // Entry requirements
  entryRequirements: EventEntryRequirements;

  // Card integration
  cardIntegration: CardIntegration;

  // Prizes
  prizePool: LeaguePrizePool;

  // Status
  status: 'UPCOMING' | 'OPEN' | 'LOCKED' | 'LIVE' | 'SCORING' | 'COMPLETED';

  // Metadata
  createdAt: Date;
}

export interface EventEntry {
  id: string;
  eventId: string;
  userId: string;

  // Entry payment
  ticketsPaid: number;
  coinsPaid: number;

  // Picks/lineup
  picks?: PickemPick[];         // For PICKEM format
  lineup?: Record<string, unknown>; // For SHOWDOWN format
  teamPick?: string;            // For SURVIVOR format

  // Results
  score: number;
  rank?: number;
  prizeWon: boolean;
  prizeDescription?: string;

  submittedAt: Date;
}

// Weekly event templates
export const WEEKLY_EVENT_TEMPLATES: Record<string, Partial<Event>> = {
  PICKEM_FREE: {
    name: "Weekly Pick'em",
    category: 'WEEKLY',
    format: 'PICKEM',
    tier: 'STANDARD',
    unlimitedEntries: true,
    cardIntegration: 'NONE',
    entryRequirements: {
      tier: 'STANDARD',
      ticketCost: 0,
      coinCost: 0,
      minCollectorScore: 0,
      cardOwnershipRequired: false,
    },
  },
  PICKEM_PREMIUM: {
    name: "Premium Pick'em",
    category: 'WEEKLY',
    format: 'PICKEM',
    tier: 'PREMIUM',
    maxEntries: 5000,
    unlimitedEntries: false,
    cardIntegration: 'OPTIONAL_BOOST',
    entryRequirements: {
      tier: 'PREMIUM',
      ticketCost: 2,
      coinCost: 0,
      minCollectorScore: 0,
      cardOwnershipRequired: false,
    },
  },
  SHOWDOWN_FREE: {
    name: 'Weekly Showdown',
    category: 'WEEKLY',
    format: 'SHOWDOWN',
    tier: 'STANDARD',
    unlimitedEntries: true,
    cardIntegration: 'NONE',
    entryRequirements: {
      tier: 'STANDARD',
      ticketCost: 0,
      coinCost: 250,
      minCollectorScore: 0,
      cardOwnershipRequired: false,
    },
  },
  SHOWDOWN_PREMIUM: {
    name: 'Premium Showdown',
    category: 'WEEKLY',
    format: 'SHOWDOWN',
    tier: 'PREMIUM',
    maxEntries: 2000,
    unlimitedEntries: false,
    cardIntegration: 'OPTIONAL_BOOST',
    entryRequirements: {
      tier: 'PREMIUM',
      ticketCost: 5,
      coinCost: 0,
      minCollectorScore: 0,
      cardOwnershipRequired: false,
    },
  },
  THURSDAY_NIGHT_SPECIAL: {
    name: 'Thursday Night Special',
    category: 'WEEKLY',
    format: 'PICKEM',
    tier: 'STANDARD',
    unlimitedEntries: true,
    cardIntegration: 'NONE',
    entryRequirements: {
      tier: 'STANDARD',
      ticketCost: 0,
      coinCost: 0,
      minCollectorScore: 0,
      cardOwnershipRequired: false,
    },
  },
  MONDAY_NIGHT_FINALE: {
    name: 'Monday Night Finale',
    category: 'WEEKLY',
    format: 'PICKEM',
    tier: 'STANDARD',
    maxEntries: 10000,
    unlimitedEntries: false,
    cardIntegration: 'NONE',
    entryRequirements: {
      tier: 'STANDARD',
      ticketCost: 0,
      coinCost: 100,
      minCollectorScore: 0,
      cardOwnershipRequired: false,
    },
  },
};

// Special event templates
export const SPECIAL_EVENT_TEMPLATES: Record<string, Partial<Event>> = {
  DRAFT_NIGHT: {
    name: 'Draft Night Mania',
    category: 'SPECIAL',
    format: 'PICKEM',
    tier: 'PREMIUM',
    maxEntries: 5000,
    cardIntegration: 'NONE',
  },
  PLAYOFF_PREDICTOR: {
    name: 'Playoff Predictor',
    category: 'SPECIAL',
    format: 'BRACKET',
    tier: 'PREMIUM',
    maxEntries: 10000,
    cardIntegration: 'OPTIONAL_BOOST',
  },
  SUPER_BOWL_SHOWDOWN: {
    name: 'Super Bowl Showdown',
    category: 'SPECIAL',
    format: 'SHOWDOWN',
    tier: 'ELITE',
    maxEntries: 2000,
    cardIntegration: 'REQUIRED',
  },
};

// Tournament templates
export const TOURNAMENT_TEMPLATES: Record<string, Partial<Event>> = {
  GRADED_CARD_SERIES: {
    name: 'Graded Card Series',
    category: 'TOURNAMENT',
    format: 'SHOWDOWN',
    tier: 'PREMIUM',
    maxEntries: 500,
    cardIntegration: 'OPTIONAL_BOOST',
    entryRequirements: {
      tier: 'PREMIUM',
      ticketCost: 5,
      coinCost: 0,
      minCollectorScore: 0,
      cardOwnershipRequired: false,
    },
  },
  COLLECTORS_CUP: {
    name: "Collector's Cup",
    category: 'TOURNAMENT',
    format: 'ACCUMULATOR',
    tier: 'ELITE',
    maxEntries: 1000,
    cardIntegration: 'REQUIRED',
    entryRequirements: {
      tier: 'ELITE',
      ticketCost: 10,
      coinCost: 0,
      minCollectorScore: 500,
      cardOwnershipRequired: true,
    },
  },
  PRIME_CHAMPIONSHIP: {
    name: 'Prime Championship',
    category: 'TOURNAMENT',
    format: 'BRACKET',
    tier: 'ELITE',
    maxEntries: 500,
    cardIntegration: 'REQUIRED',
    entryRequirements: {
      tier: 'ELITE',
      ticketCost: 25,
      coinCost: 0,
      minCollectorScore: 2000,
      cardOwnershipRequired: true,
    },
  },
};

// Legacy Tournament interface (keeping for compatibility)
export interface Tournament extends Event {
  entriesPerPack: number;
  prizeType: 'GRADED_CARD' | 'VIRTUAL' | 'MIXED';
  prizeDescription: string;
  prizeValue: number;
  prizeImageUrl?: string;
}

export interface TournamentEntry extends EventEntry {
  sourcePackId?: string;
}

// ==================== NOTIFICATIONS ====================

export type NotificationType = 'SYSTEM' | 'ACHIEVEMENT' | 'MARKET' | 'SOCIAL' | 'GAME';

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  notificationType: NotificationType;
  metadata?: Record<string, unknown>;
  isRead: boolean;
  createdAt: Date;
}

// ==================== XP CONSTANTS ====================

export const XP_CONFIG = {
  LOGIN: 10,
  PICKEM_SELECTION: 50,
  CHAT_MESSAGE: 25,
  PACK_OPEN: 100,
  TRADE_COMPLETE: 75,
  REFERRAL: 500,
  WIN_PICKEM: 200,
  STREAK_MILESTONE_7: 250,
  STREAK_MILESTONE_30: 1000,
  STREAK_MILESTONE_100: 5000,
  STREAK_MILESTONE_365: 25000,
} as const;

export const LEVEL_XP_REQUIREMENT = 1000; // XP per level

// ==================== RETURNING PLAYER BONUSES ====================

export const RETURNING_BONUSES = {
  7: { coins: 500, packType: 'STANDARD' as PackType, xpMultiplier: 1.5, duration: 24 },
  30: { coins: 2000, packType: 'PREMIUM' as PackType, xpMultiplier: 2.0, duration: 72 },
  90: { coins: 5000, packType: 'LEGENDARY' as PackType, xpMultiplier: 3.0, duration: 168 },
} as const;
